import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, getDocFromServer, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchGameStats, fetchGameDailyStats } from '../services/gameAnalytics';
import DefaultLoading from '../common/DefaultLoading';
import AlertError from '../dashboard/PermissionDenied';
import { ChartContent } from '../dashboard/stats';
import { motion, AnimatePresence } from 'framer-motion';
import FreshnessIndicator from '../components/FreshnessIndicator';
import { buildChartData } from '../utils/chartUtils';
import {
  PlayIcon,
  ShareIcon,
  CursorArrowRaysIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const POLL_INTERVAL = 15000;

const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};


const CampaignResultsPage = () => {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [chartView, setChartView] = useState('7days');
  const [showHourlyDetail, setShowHourlyDetail] = useState(true);
  const [visibleDatasets, setVisibleDatasets] = useState({ Plays: true });
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [chartTotals, setChartTotals] = useState({ plays: 0 });
  const [insightsTab, setInsightsTab] = useState('devices');
  const pollActiveRef = useRef(true);

  // Paint body to match page background so no white shows on scroll
  useEffect(() => {
    document.documentElement.style.backgroundColor = '#3B4F5E';
    document.body.style.backgroundColor = '#3B4F5E';
    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Password gate state
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordUnlocked, setPasswordUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [campaignRaw, setCampaignRaw] = useState(null); // holds campaign before password check

  // Fetch campaign data once
  useEffect(() => {
    if (!id) {
      setError('No campaign ID provided');
      setLoading(false);
      return;
    }

    const fetchCampaign = async () => {
      try {
        const campaignDoc = await getDocFromServer(doc(db, 'campaign', id));
        if (!campaignDoc.exists()) {
          setError('Campaign not found');
          setLoading(false);
          return;
        }

        const campaignData = { id: campaignDoc.id, ...campaignDoc.data() };

        if (campaignData.resultsPasswordHash) {
          // Check sessionStorage for existing unlock
          const sessionKey = `results_unlocked_${id}`;
          const stored = sessionStorage.getItem(sessionKey);
          if (stored === campaignData.resultsPasswordHash) {
            setCampaign(campaignData);
            setPasswordUnlocked(true);
          } else {
            setCampaignRaw(campaignData);
            setPasswordRequired(true);
          }
        } else {
          // No password generated yet — results not shared
          setError('Results are not available for this campaign yet.');
        }
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError('Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordInput.trim() || !campaignRaw) return;
    setIsCheckingPassword(true);
    setPasswordError('');
    try {
      const inputHash = await hashPassword(passwordInput.trim());
      if (inputHash === campaignRaw.resultsPasswordHash) {
        sessionStorage.setItem(`results_unlocked_${id}`, inputHash);
        setCampaign(campaignRaw);
        setPasswordUnlocked(true);
        setPasswordRequired(false);
      } else {
        setPasswordError('Incorrect password');
      }
    } catch (err) {
      setPasswordError('Something went wrong');
    } finally {
      setIsCheckingPassword(false);
    }
  };

  // Poll stats + leaderboard
  const fetchLiveData = useCallback(async () => {
    if (!id || !campaign) return;

    try {
      const gameStats = await fetchGameStats(id);
      if (!pollActiveRef.current) return;
      setStats(gameStats);
      setLastUpdated(new Date());

      if (campaign.options?.leaderboard?.enabled) {
        const leaderboardRef = collection(db, 'leaderboard', id, 'entries');
        const leaderboardQuery = query(leaderboardRef, orderBy('score', 'desc'), limit(10));
        const snapshot = await getDocs(leaderboardQuery);
        if (!pollActiveRef.current) return;
        setLeaderboardEntries(
          snapshot.docs.map((d, index) => ({
            id: d.id,
            ...d.data(),
            rank: index + 1,
          }))
        );
      }
    } catch (err) {
      console.error('Error polling live data:', err);
    }
  }, [id, campaign]);

  useEffect(() => {
    if (!campaign) return;
    pollActiveRef.current = true;

    fetchLiveData();
    const interval = setInterval(fetchLiveData, POLL_INTERVAL);

    return () => {
      pollActiveRef.current = false;
      clearInterval(interval);
    };
  }, [campaign, fetchLiveData]);

  // Fetch daily stats for chart (always 7 days — covers both today and 7-day views)
  useEffect(() => {
    if (!id || !campaign) return;
    fetchGameDailyStats(id, 7).then(setDailyStats).catch(console.error);
  }, [id, campaign]);

  // Build chart data from daily stats (plays only)
  useEffect(() => {
    if (!dailyStats.length) return;

    const { playCounts, chartLabels } = buildChartData(dailyStats, chartView, showHourlyDetail);

    setChartData({
      labels: chartLabels,
      datasets: [
        {
          label: 'Plays',
          data: playCounts,
          borderColor: '#9B59B6',
          backgroundColor: 'rgba(155, 89, 182, 0.2)',
          fill: true,
          borderWidth: 3,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointBackgroundColor: '#9B59B6',
        },
      ],
    });
    setChartTotals({ plays: playCounts.reduce((s, v) => s + v, 0) });
  }, [dailyStats, chartView, showHourlyDetail]);


  if (loading) return <div className="admin-theme min-h-screen gch-layout-bg"><DefaultLoading /></div>;
  if (error) return <div className="admin-theme min-h-screen gch-layout-bg"><AlertError text={error} /></div>;

  // Password gate
  if (passwordRequired && !passwordUnlocked) {
    const gateTitle = campaignRaw?.options?.title || 'Campaign Results';
    return (
      <div className="admin-theme min-h-screen gch-layout-bg flex items-center justify-center px-4">
        <div className="gch-card w-full max-w-sm px-6 py-8">
          <div className="text-center mb-6">
            <h1 className="text-lg font-bold gch-text-primary tracking-tight">{gateTitle}</h1>
            <p className="text-sm gch-text-secondary mt-1">Enter password to view results</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(''); }}
                placeholder="Password"
                autoFocus
                className="w-full px-4 py-3 rounded-lg text-sm gch-text-primary gch-input-ghost border border-[var(--admin-border)] focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)] placeholder:gch-text-muted"
              />
              {passwordError && (
                <p className="text-xs text-red-400 mt-1.5">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isCheckingPassword || !passwordInput.trim()}
              className="w-full btn-admin-primary btn-admin-md disabled:opacity-50"
            >
              {isCheckingPassword ? 'Checking...' : 'View results'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!campaign) return <div className="admin-theme min-h-screen gch-layout-bg"><AlertError text="Campaign not found" /></div>;

  const totalPlays = stats?.totalPlays || 0;
  const totalCompletions = stats?.totalCompletions || 0;
  const totalShares = stats?.totalShares || 0;
  const totalCtas = stats?.totalCtas || 0;
  const totalLeaderboardJoins = stats?.totalLeaderboardJoins || 0;
  const totalEmailCaptures = stats?.totalEmailCaptures || 0;
  const completionRate = stats?.completionRate || 0;
  const shareRate = stats?.shareRate || 0;
  const ctaRate = stats?.ctaRate || 0;
  const avgDurationSeconds = stats?.avgDurationSeconds || 0;
  const devices = stats?.devices || {};
  const title = campaign.options?.title || 'Untitled Campaign';

  // Device totals for percentage calc
  const totalDevices = (devices.desktop || 0) + (devices.mobile || 0) + (devices.tablet || 0);
  const devicePct = (count) => totalDevices > 0 ? Math.round((count / totalDevices) * 100) : 0;

  // Aggregate traffic sources from daily stats
  const aggregatedReferrers = (() => {
    const isSelfReferrer = (r) => /^(direct|localhost|.*smilesflow\..*)$/i.test(r);
    const counts = {};
    dailyStats.forEach((d) => {
      (d.topReferrers || []).forEach((r) => {
        if (r && !isSelfReferrer(r)) counts[r] = (counts[r] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, days]) => ({ name, days }));
  })();

  const aggregatedUtmSources = (() => {
    const counts = {};
    dailyStats.forEach((d) => {
      (d.topUtmSources || []).forEach((u) => {
        if (u) counts[u] = (counts[u] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, days]) => ({ name, days }));
  })();

  const hasTrafficSources = aggregatedReferrers.length > 0 || aggregatedUtmSources.length > 0;


  return (
    <>
      <Helmet>
        <title>{title} - Results | SmilesFlow</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta
          name="description"
          content={`${totalPlays.toLocaleString()} plays, ${completionRate}% completion rate`}
        />
        <meta property="og:title" content={`${title} - Campaign Results`} />
        <meta
          property="og:description"
          content={`${totalPlays.toLocaleString()} plays, ${completionRate}% completion rate`}
        />
        {campaign.options?.coverPhoto && (
          <meta property="og:image" content={campaign.options.coverPhoto} />
        )}
      </Helmet>

      <div className="admin-theme min-h-screen gch-layout-bg px-4 pt-6 pb-24 sm:pt-8 sm:pb-32">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Header */}
          <div className="gch-card px-5 sm:px-6 py-5">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold gch-text-primary tracking-tight truncate">
                  {title}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-xs gch-text-secondary">Campaign Performance</p>
                  {lastUpdated && (
                    <FreshnessIndicator timestamp={lastUpdated.getTime()} />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a
                  href={`${window.location.origin}/p/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-admin-primary btn-admin-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  <PlayIcon className="w-4 h-4" />
                  Play Game
                </a>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.origin + '/p/' + id)}&bgcolor=2E3D4A&color=ffffff&margin=8`}
                  alt="QR Code"
                  className="w-14 h-14 rounded-lg hidden sm:block"
                />
              </div>
            </div>
          </div>

          {/* Main grid: Chart (8) + Sidebar (4) */}
          <div className="grid grid-cols-12 gap-6">

            {/* Main column */}
            <div className="col-span-12 lg:col-span-8 space-y-6">

              {/* Chart — reuses ChartContent from admin Stats */}
              <ChartContent
                chartData={chartData}
                chartView={chartView}
                onChartViewChange={setChartView}
                isChartSwitching={false}
                isUsingCache={false}
                showHourlyDetail={showHourlyDetail}
                onHourlyDetailChange={() => setShowHourlyDetail((prev) => !prev)}
                visibleDatasets={visibleDatasets}
                onVisibleDatasetsChange={setVisibleDatasets}
                chartTotals={chartTotals}
                hideToggles
              />

              {/* Insights (Devices + Traffic Sources + Engagement) */}
              {(
                <div className="gch-card overflow-hidden">
                  <header className="px-5 py-4 border-b border-[var(--admin-border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <h2 className="gch-heading-sm tracking-tight">INSIGHTS</h2>
                    <div className="flex rounded-lg p-1 gch-bg-card border border-[var(--admin-border)] self-start sm:self-auto">
                      <button
                        onClick={() => setInsightsTab('devices')}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${insightsTab === 'devices' ? 'bg-[var(--admin-cta-solid)] text-white' : 'gch-text-secondary hover:bg-white/10'}`}
                      >Devices</button>
                      <button
                        onClick={() => setInsightsTab('sources')}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${insightsTab === 'sources' ? 'bg-[var(--admin-cta-solid)] text-white' : 'gch-text-secondary hover:bg-white/10'}`}
                      >Traffic Sources</button>
                      <button
                        onClick={() => setInsightsTab('engagement')}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${insightsTab === 'engagement' ? 'bg-[var(--admin-cta-solid)] text-white' : 'gch-text-secondary hover:bg-white/10'}`}
                      >Engagement</button>
                    </div>
                  </header>
                  <div className="px-5 py-4 pb-6">
                    {insightsTab === 'devices' && totalDevices > 0 && (
                      <div className="space-y-3">
                        {[
                          { name: 'Mobile', count: devices.mobile || 0, icon: DevicePhoneMobileIcon },
                          { name: 'Desktop', count: devices.desktop || 0, icon: ComputerDesktopIcon },
                          { name: 'Tablet', count: devices.tablet || 0, icon: DeviceTabletIcon },
                        ].map(({ name, count, icon: Icon }) => {
                          const pct = devicePct(count);
                          return (
                            <div key={name}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="flex items-center gap-2 text-xs gch-text-secondary">
                                  <Icon className="w-3.5 h-3.5" />
                                  {name}
                                </span>
                                <span className="text-xs font-semibold tabular-nums gch-text-primary">
                                  {count.toLocaleString()} ({pct}%)
                                </span>
                              </div>
                              <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: 'var(--admin-accent)' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {insightsTab === 'devices' && totalDevices === 0 && (
                      <p className="text-xs gch-text-muted">No device data yet.</p>
                    )}
                    {insightsTab === 'sources' && (
                      <div className="space-y-4">
                        {!hasTrafficSources && (
                          <p className="text-xs gch-text-muted">No traffic source data yet.</p>
                        )}
                        {aggregatedReferrers.length > 0 && (
                          <div>
                            <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-2">Referrers</p>
                            <div className="space-y-1.5">
                              {aggregatedReferrers.map(({ name }) => (
                                <div key={name} className="flex items-center justify-between">
                                  <span className="text-xs gch-text-secondary truncate">{name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {aggregatedUtmSources.length > 0 && (
                          <div className={aggregatedReferrers.length > 0 ? 'pt-3 border-t border-white/5' : ''}>
                            <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-2">UTM Sources</p>
                            <div className="space-y-1.5">
                              {aggregatedUtmSources.map(({ name }) => (
                                <div key={name} className="flex items-center justify-between">
                                  <span className="text-xs gch-text-secondary truncate">{name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {insightsTab === 'engagement' && (
                      <div className="space-y-5">
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-3">Rates</p>
                          <div className="space-y-4">
                            {[
                              { label: 'Completion Rate', value: completionRate, color: '#2ECC71', sub: 'of plays' },
                              { label: 'Share Rate', value: shareRate, color: '#F59E0B', sub: 'of completions' },
                              { label: 'CTA Click Rate', value: ctaRate, color: '#3498DB', sub: 'of completions' },
                            ].map(({ label, value, color, sub }) => (
                              <div key={label}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-xs font-medium gch-text-secondary">
                                    {label}
                                    {sub && <span className="gch-text-muted ml-1 font-normal">({sub})</span>}
                                  </span>
                                  <span className="text-sm font-bold tabular-nums" style={{ color }}>
                                    {value}%
                                  </span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all duration-700 ease-out"
                                    style={{
                                      width: `${Math.max(value, 0)}%`,
                                      backgroundColor: color,
                                      opacity: 0.85,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="pt-3 border-t border-white/5">
                          <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mb-2">Details</p>
                          <div className="space-y-2.5">
                            {[
                              { name: 'Shares', value: totalShares.toLocaleString(), icon: ShareIcon },
                              { name: 'Avg Duration', value: avgDurationSeconds > 0 ? `${avgDurationSeconds}s` : '-', icon: ClockIcon },
                            ].map(({ name, value, icon: Icon }) => (
                              <div key={name} className="flex items-center justify-between">
                                <span className="flex items-center gap-2 text-xs gch-text-secondary">
                                  <Icon className="w-3.5 h-3.5" />
                                  {name}
                                </span>
                                <span className="text-sm font-semibold tabular-nums gch-text-primary">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Sidebar */}
            <div className="col-span-12 lg:col-span-4 space-y-4">

              {/* Key Metrics — 2x2 mini cards */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Total Plays', value: totalPlays, icon: PlayIcon, color: '#9B59B6' },
                  { label: 'Completions', value: totalCompletions, icon: CheckCircleIcon, color: '#2ECC71' },
                  { label: 'CTA Clicks', value: totalCtas, icon: CursorArrowRaysIcon, color: '#3498DB' },
                  { label: 'Entries', value: totalLeaderboardJoins, icon: UserGroupIcon, color: '#E56A54' },
                  { label: 'Email Captures', value: totalEmailCaptures, icon: EnvelopeIcon, color: '#F59E0B' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="gch-card px-4 py-3.5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                    </div>
                    <p className="text-xl font-bold tabular-nums gch-text-primary leading-none">
                      {typeof value === 'number' ? value.toLocaleString() : value}
                    </p>
                    <p className="text-[10px] font-medium uppercase tracking-wider gch-text-muted mt-1.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Leaderboard */}
              {campaign.options?.leaderboard?.enabled && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="gch-card overflow-hidden"
                >
                  <header className="px-5 py-4 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="gch-heading-sm tracking-tight">LEADERBOARD</h2>
                        <p className="text-xs gch-text-secondary mt-1 tabular-nums">
                          Top 10 players
                        </p>
                      </div>
                    </div>
                  </header>

                  <div className="pt-0 pb-2">
                    {leaderboardEntries.length === 0 ? (
                      <div className="mx-4 my-3 text-center rounded-lg gch-input-ghost px-4 py-3">
                        <p className="text-xs gch-text-muted">No participants yet</p>
                      </div>
                    ) : (
                      <AnimatePresence>
                        {leaderboardEntries.map((entry, index) => {
                          const rank = index + 1;
                          return (
                            <motion.div
                              key={entry.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="flex items-center justify-between gap-4 px-5 py-2.5 hover:bg-white/5 transition-colors"
                            >
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div className="text-sm font-medium w-7 shrink-0 gch-text-secondary tabular-nums">
                                  {rank}.
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium text-sm gch-text-primary truncate block">
                                    {entry.name || 'Anonymous'}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-semibold text-sm tabular-nums gch-text-primary">
                                  {entry.score?.toLocaleString() || 0}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    )}
                  </div>
                </motion.div>
              )}

            </div>
          </div>

          {/* Bottom spacer */}
          <div className="h-24 sm:h-32" aria-hidden="true" />

        </div>
      </div>
    </>
  );
};

export default CampaignResultsPage;
